import { normalCDF } from 'helpers/normal-distribution';

/**
 * Turns daily counts into the three series the page needs.
 *
 * All three answer questions a single cumulative number cannot. Has the rate settled,
 * or is it still moving? Did the variant win from the start or only after a novelty
 * period? And did significance hold once crossed, or flicker across the line, which
 * is what makes stopping on the first significant day a mistake.
 *
 * Rates are only produced where sessions exist. visitor_count_hourly begins when that
 * rollup shipped, and a day with conversions but no session count would otherwise
 * divide by zero and draw a spike.
 *
 * Days from before the rollup are excluded entirely, not just suppressed: their
 * conversions have no denominator that will ever arrive, and folding them into the
 * cumulative totals put the whole pre-rollup conversion history over post-rollup
 * sessions only, inflating every rate from the first drawable day onwards.
 */
export type DailyCount = {
  date: string;
  variantId: number;
  sessions: number;
  conversions: number;
  revenue: number;
};

export type CumulativePoint = {
  date: string;
  /** Cumulative rate per variant, keyed by variant id. Absent where sessions are 0. */
  rates: Record<number, number | undefined>;
  /** 95% Wald half-widths, for the confidence band. */
  margins: Record<number, number | undefined>;
  sessions: Record<number, number>;
};

export type DailyRatePoint = { date: string; rates: Record<number, number | undefined> };

export type SignificancePoint = { date: string; pValue: number | null; significant: boolean };

const Z_95 = 1.959963984540054;

/** Wald half-width for a proportion. Wide but symmetric, which is what a band needs. */
const margin = (rate: number, n: number): number | undefined =>
  n > 0 ? Z_95 * Math.sqrt(Math.max(0, rate * (1 - rate)) / n) : undefined;

const uniqueDates = (points: readonly DailyCount[]): string[] => [...new Set(points.map((p) => p.date))].sort();

const byDate = (points: readonly DailyCount[]) => {
  const map = new Map<string, DailyCount[]>();
  for (const point of points) {
    const list = map.get(point.date);
    if (list) list.push(point);
    else map.set(point.date, [point]);
  }
  return map;
};

/**
 * Drops days from before session history begins.
 *
 * The timeseries join returns conversions for the experiment's whole life but sessions
 * only from the hourly rollup onwards, so earlier days arrive as conversions with zero
 * sessions. Apply this before any of the builders below: without it those conversions
 * enter the cumulative numerators while their sessions never arrive. A null start
 * means no session history exists at all, so nothing is drawable.
 */
export const sinceSessionsStart = (points: readonly DailyCount[], sessionsFrom: string | null): DailyCount[] =>
  sessionsFrom === null ? [] : points.filter((point) => point.date >= sessionsFrom);

/**
 * Cumulative conversion rate per variant with a confidence band.
 *
 * The chart that answers "can I stop yet": bands that have narrowed and separated mean
 * the result is settled, bands still overlapping mean it is not, however far apart the
 * point estimates look.
 */
export const buildCumulativeSeries = (
  points: readonly DailyCount[],
  variantIds: readonly number[],
): CumulativePoint[] => {
  const grouped = byDate(points);
  const totals = new Map<number, { sessions: number; conversions: number }>();
  variantIds.forEach((id) => totals.set(id, { sessions: 0, conversions: 0 }));

  return uniqueDates(points).map((date) => {
    for (const point of grouped.get(date) ?? []) {
      const total = totals.get(point.variantId);
      if (!total) continue;
      total.sessions += point.sessions;
      total.conversions += point.conversions;
    }

    const rates: Record<number, number | undefined> = {};
    const margins: Record<number, number | undefined> = {};
    const sessions: Record<number, number> = {};

    for (const id of variantIds) {
      const total = totals.get(id)!;
      sessions[id] = total.sessions;
      if (total.sessions > 0) {
        const rate = total.conversions / total.sessions;
        rates[id] = rate;
        margins[id] = margin(rate, total.sessions);
      }
    }

    return { date, rates, margins, sessions };
  });
};

/**
 * Per-day rate rather than cumulative.
 *
 * Cumulative series flatten by construction, each day is diluted by all the days
 * before it, so a variant that wins in week one and reverts by week three still looks
 * like a mild winner. The daily view is the only one where that shows.
 */
export const buildDailySeries = (points: readonly DailyCount[], variantIds: readonly number[]): DailyRatePoint[] => {
  const grouped = byDate(points);

  return uniqueDates(points).map((date) => {
    const rates: Record<number, number | undefined> = {};
    for (const id of variantIds) {
      const point = (grouped.get(date) ?? []).find((p) => p.variantId === id);
      if (point && point.sessions > 0) rates[id] = point.conversions / point.sessions;
    }
    return { date, rates };
  });
};

/**
 * The p-value recomputed at each day, against the control.
 *
 * Reveals peeking directly: a series that dips below 0.05 and comes back up shows that
 * stopping on the first significant day would have been stopping on noise. Uses the
 * same pooled two-proportion test as the significance table so the last point matches
 * the number reported there.
 */
export const buildSignificanceSeries = (
  points: readonly DailyCount[],
  controlId: number,
  variantId: number,
): SignificancePoint[] => {
  const grouped = byDate(points);
  let control = { sessions: 0, conversions: 0 };
  let variant = { sessions: 0, conversions: 0 };

  return uniqueDates(points).map((date) => {
    for (const point of grouped.get(date) ?? []) {
      if (point.variantId === controlId) {
        control = { sessions: control.sessions + point.sessions, conversions: control.conversions + point.conversions };
      } else if (point.variantId === variantId) {
        variant = { sessions: variant.sessions + point.sessions, conversions: variant.conversions + point.conversions };
      }
    }

    if (control.sessions === 0 || variant.sessions === 0) return { date, pValue: null, significant: false };

    const p1 = control.conversions / control.sessions;
    const p2 = variant.conversions / variant.sessions;
    const pooled = (control.conversions + variant.conversions) / (control.sessions + variant.sessions);
    const se = Math.sqrt(pooled * (1 - pooled) * (1 / control.sessions + 1 / variant.sessions));

    if (se === 0) return { date, pValue: null, significant: false };

    const z = (p2 - p1) / se;
    const pValue = 2 * (1 - normalCDF(Math.abs(z)));
    return { date, pValue, significant: pValue < 0.05 };
  });
};

/**
 * How many times the series crossed the 0.05 line.
 *
 * More than one crossing is the concrete evidence that an early stop would have been
 * premature, which is more persuasive than a general warning about peeking.
 */
export const countSignificanceCrossings = (series: readonly SignificancePoint[]): number => {
  let crossings = 0;
  let previous: boolean | null = null;

  for (const point of series) {
    if (point.pValue === null) continue;
    if (previous !== null && point.significant !== previous) crossings++;
    previous = point.significant;
  }

  return crossings;
};
